import React, {
  useReducer,
  useCallback,
  useContext,
  useEffect,
  useMemo
} from 'react';
import useSkipUpdate from '../../../hooks/useSkipRender';
import fetchAPI from '../../../utils/fetchAPI';
import AutomationsTriggerView from '../view/AutomationsTriggerView';
import { AutomationsWizardContext } from './AutomationWizardEngine';
import { AutomationsContex } from '../../engine/FormBuilderAutomationsEngine';
import _isEqual from 'lodash.isequal';
import { getAllFields } from '../../../internal-components/form-builder/getAllFields';

const timeBasedDateFieldMap = {
  time_since_creation: 'createdDate',
  time_since_last_update: 'updatedDate'
};

export const TIME_BASED_TRIGGERS = [
  'time_since_creation',
  'time_since_last_update',
  'time_since_last_status_change',
  'date_field'
];

const TriggersReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };

    case 'update_scheduler':
      return {
        ...state,
        schedulerData: { ...state.schedulerData, ...payload }
      };
    case 'delete_scheduler':
      return {
        ...state,
        schedulerData: {}
      };
    default:
      return state;
  }
};

/**
 *  A dedicated comparison function for shceduler data based on trigger usage.
 *  We will compare only the dymanic parts of the nodes which are getting modified
 *  in this component.
 *
 * @param {Node} oldSc - Old scheduler data node
 * @param {Node} newSc - New scheduler data node
 * @returns true if they something changed
 */
const isUpdateNeeded = (oldSc, newSc) => {
  const isIdChanged = oldSc.id !== newSc.id;
  const isDateFieldChanged = oldSc.dateFieldName !== newSc.dateFieldName;
  const isConditionChanged =
    JSON.stringify(oldSc.condition) !== JSON.stringify(newSc.condition);
  const isLengthEqual = Object.keys(oldSc).length === Object.keys(newSc).length;

  return (
    isIdChanged || isDateFieldChanged || isConditionChanged || !isLengthEqual
  );
};

export const generateDeleteSchedulerPromise = (schdulerId, handleResponse) => {
  return () =>
    new Promise((resolve, reject) => {
      fetchAPI(`/scheduler/v1/schedule/${schdulerId}`, {
        method: 'DELETE',
        handleResponse
      })
        .then(resolve)
        .catch(reject);
    });
};

const AutomationsTriggerEngine = ({
  trigger,
  onTriggerChange: setTrigger = () => {},
  schedulerData = {},
  setSchedulerPromise = () => {}
}) => {
  const [data, dispatch] = useReducer(TriggersReducer, {
    schedulerData
  });
  const {
    form,
    updateScedulerInState,
    deleteSchedulerInState,
    hasWorldcheck,
    hasCredas,
    hasThirdfort
  } = useContext(AutomationsContex);
  const { automationData } = useContext(AutomationsWizardContext);
  const { shouldSkipUpdate, skipUpdate } = useSkipUpdate();

  const dateFields = useMemo(() => {
    const _dateFields = [];
    if (!form) {
      return _dateFields;
    }

    const allFields = getAllFields(form);
    allFields.forEach(f => {
      if (f.uiType === 'date') {
        _dateFields.push(f);
      }
    });

    return _dateFields;
  }, [form]);

  const onTriggerChange = useCallback(
    value => {
      if (/\d/.test(value)) {
        // value containes numbers - it's an id which means it's a custom button trigger
        setTrigger('cb', value);
      } else {
        setTrigger(value);
      }

      const schedulerId = data.schedulerData.id;

      if (!TIME_BASED_TRIGGERS.includes(value)) {
        dispatch({ type: 'delete_scheduler' });
        skipUpdate();
        if (schedulerId) {
          setSchedulerPromise(
            generateDeleteSchedulerPromise(schedulerId, () =>
              deleteSchedulerInState(data.schedulerData.automationId)
            )
          );
        } else {
          setSchedulerPromise(() => {});
        }
      } else if (value === 'time_since_last_status_change' && schedulerId) {
        // we changed to status based scheduler from time based scheduler
        dispatch({
          type: 'update_scheduler',
          payload: {
            ...data.schedulerData,
            condition: {
              ...data.schedulerData.condition,
              conditionType: 'status'
            }
          }
        });
      } else if (schedulerId) {
        // we changed to date based scheduler from status based scheduler
        dispatch({
          type: 'update_scheduler',
          payload: {
            ...data.schedulerData,
            condition: {
              ...data.schedulerData.condition,
              conditionType: 'date'
            }
          }
        });
      }
    },
    [data]
  );

  const onTriggerSchedulerChange = useCallback(
    dataBlock => {
      dispatch({ type: 'update_scheduler', payload: dataBlock });
    },
    [form]
  );

  useEffect(() => {
    if (shouldSkipUpdate()) {
      return;
    }

    const shceduler = data.schedulerData;
    const promiseType = shceduler.id || schedulerData.id ? 'PUT' : 'POST';
    const urlPostfix = shceduler.id ? `/${shceduler.id}` : '';
    const dateFieldName =
      data.schedulerData.condition.dateFieldName ||
      timeBasedDateFieldMap[trigger];

    const body = {
      condition: {},
      ...shceduler,
      jobType: 'automations',
      assetId: form.id,
      automationId: automationData.internalId,
      title: '',
      isActive: true,
      id: data.schedulerData.id || schedulerData.id || undefined
    };
    body.condition.dateFieldName = dateFieldName;

    skipUpdate();
    dispatch({ type: 'update_scheduler', payload: body });

    const setSchedulerData = () =>
      new Promise((resolve, reject) => {
        fetchAPI(`/scheduler/v1/schedule${urlPostfix}`, {
          method: promiseType,
          body,
          handleResponse: r => {
            const id = r.headers.map.location;
            //add id before updateScedulerInState
            if (!body.id) {
              body.id = id;
            }
            updateScedulerInState({
              ...body,
              id: data.schedulerData.id || schedulerData.id || id
            });
            resolve();
          }
        }).catch(reject);
      });

    if (_isEqual(body, schedulerData)) {
      // if nothing changed from the original data - nullify the promise
      setSchedulerPromise(() => {});
    }

    setSchedulerPromise(setSchedulerData);
  }, [data]);

  const viewProps = {
    hasWorldcheck,
    hasThirdfort,
    hasCredas,
    trigger,
    onTriggerChange,
    onTriggerSchedulerChange,
    schedulerData:
      JSON.stringify(data.schedulerData) === '{}'
        ? undefined
        : data.schedulerData,
    showStatusBased: trigger === 'time_since_last_status_change',
    showDateBased:
      trigger === 'time_since_last_update' || trigger === 'time_since_creation',
    showDateFieldBased: trigger === 'date_field',
    customButtons: (form.buttons && (form.buttons.customButtons || [])) || [],
    dateFields
  };

  return <AutomationsTriggerView {...viewProps} />;
};

export default AutomationsTriggerEngine;
